import { useInfiniteQuery } from '@tanstack/react-query';
import { Request } from 'crono-fe-common/types/request';
import { ENDPOINTS } from 'config/endpoints';
import client from 'utils/clients/client';
import CronoError from 'crono-fe-common/types/error';
import { ResponsePaginated } from 'crono-fe-common/types/response';
import ConversationDirection from 'crono-fe-common/types/ConversationDirection';
import { ConversationMessage } from 'crono-fe-common/types/ConversationMessage';
import { Constants } from 'crono-fe-common/constants/constants';
import { ConversationType } from 'crono-fe-common/types/ConversationType';

export interface IGetFullConversation {
  limit: number;
  prospectId: string;
  types?: ConversationType[] | null;
}

interface GetFullConversationResult {
  accountId: string | null;
  prospectId: string | null;
  direction: ConversationDirection;
  messages: ConversationMessage[];
  previousAccounts:
    | {
        objectId: string | null;
        name: string | null;
        website: string | null;
      }[]
    | null;
}
export const MESSAGES_PER_PAGE_INBOX = 20;

export function useGetFullConversation(listParams: IGetFullConversation) {
  const getRequest = (offset = 0) => {
    const request: Request = {
      url: ENDPOINTS.conversation.full,
      config: {
        method: 'post',
        data: {
          data: { ...listParams, page: offset / MESSAGES_PER_PAGE_INBOX + 1 },
        },
      },
    };
    return request;
  };
  const paramsString = JSON.stringify(listParams);
  const requestConfig = {
    queryKey: [
      ENDPOINTS.conversation.full,
      listParams.prospectId,
      paramsString,
    ],
    queryFn: ({ pageParam }: { pageParam?: number }) =>
      client(getRequest(pageParam)),
    getNextPageParam: (
      lastPage: ResponsePaginated<GetFullConversationResult>,
      pages: ResponsePaginated<GetFullConversationResult>[],
    ) => {
      if (!lastPage.data.data) {
        return undefined;
      }
      const howManyLoaded = pages.reduce(
        (sum, page) => sum + page.data.data.messages.length,
        0,
      );
      const checkIfHasNextPage =
        lastPage.data.data.messages.length === 0 ||
        lastPage.data.data.messages.length <
          (listParams.limit ?? Constants.defaultLimit);
      if (checkIfHasNextPage) {
        return undefined;
      }
      return howManyLoaded;
    },
    staleTime: 1 * 60 * 1000,
  };
  return useInfiniteQuery<
    ResponsePaginated<GetFullConversationResult>,
    CronoError
  >(requestConfig);
}
